import React, { useCallback, useMemo, useState } from 'react';
import { Popconfirm, Form, Space, DatePicker, Col } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { PlusOutlined } from '@ant-design/icons';
import { sumBy } from 'lodash';
import moment from 'moment';

import { toCurrency } from 'utils/utils';
import { dateToString, indexBy, shouldUpdateCompare } from 'utils/utils2';
import { useFormList } from 'hooks/useFormList';
import Value from 'components/Value';
import DataGrid from 'components/DataGrid';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { ClientInvoiceScheduleResponse } from 'services/clientInvoice';
import CIPayerSelect from './CIPayerSelect';
import InputCurrency from 'components/InputCurrency';
import ThirdPartyPayerList from './ThirdPartyPayer';
import Title from 'components/Title';
import DeleteButton from 'components/DeleteButton';
import ButtonOperation from 'components/ButtonOperation';
import ParBankAccountSelect from 'components/ParBankAccountSelect';
import GetLabel from 'DisplayFramework/components/GetLabel';
import _ from 'lodash';

type TabClientInvoiceScheduleProps = {
  form: FormInstance;
  ciid: number | undefined;
};

const TabClientInvoiceSchedule = (props: TabClientInvoiceScheduleProps) => {
  const { form, ciid } = props;

  const fieldName = 'clientInvoiceSchedules';

  const { add, remove } = useFormList(form, fieldName, {
    addAsFirst: true,
    idKey: 'cisid'
  });

  if (form.getFieldValue(fieldName) === undefined) {
    form.setFieldsValue({
      [fieldName]: []
    });
  }
  const getName = useCallback(
    (name: string) => (record: ClientInvoiceScheduleResponse) => {
      const schedules = form.getFieldValue(fieldName);
      const index = indexBy(schedules, 'cisid')[record.cisid];
      return [fieldName, index, name];
    },
    [form]
  );
  const addRow = () => {
    const totalInvoiceAmount: number = form.getFieldValue('cigrosstotal');
    const totalPaymentsScheduled: number = form.getFieldValue('cisamount');
    let leftAmount: number = totalInvoiceAmount;

    if (totalPaymentsScheduled - totalInvoiceAmount > 0) {
      leftAmount = totalPaymentsScheduled - totalInvoiceAmount;
    }
    const newcisorder = form.getFieldValue(fieldName).length + 1;

    const cisamount = totalPaymentsScheduled
      ? leftAmount - totalPaymentsScheduled
      : leftAmount;
    const item: any = {
      cisid: Math.round(_.random(10000000)),
      cisamount,
      cisdueamount: cisamount,
      cisduedate: moment() as any,
      cisorder: newcisorder,
      cispaymentdelaycode: '',
      cispaymentmode: '',
      cisstatus: 'INIT',
      cistype: 'PAYMENT',
      mgbankaccount: '',
      tpidpayer: undefined,
      tppayerbankaccount: ''
    };

    add(item);
    recalculate(0, { cisamont: cisamount });
    insertpayer(state.payerList[0], 0);
  };

  const recalculate = useCallback(
    (index: number, updatedValues: any) => {
      const values = form.getFieldValue(
        fieldName
      ) as ClientInvoiceScheduleResponse[];

      let newValues = values.map((item, i) => {
        if (i !== index) {
          return item;
        }

        const updatedItem = { ...item, ...updatedValues };

        const cisamount = updatedItem.cisamount;
        const cisdueamount = updatedItem.cisamount;

        return {
          ...updatedItem,
          cisamount,
          cisdueamount
        };
      });

      let cisamount = sumBy(newValues, 'cisamount');
      if (cisamount > form.getFieldValue('cigrosstotal')) {
        let amounttoreduce = cisamount - form.getFieldValue('cigrosstotal');
        newValues = newValues.map((item, i) => {
          if (i === index) return item;

          if (item.cisamount > amounttoreduce && amounttoreduce > 0) {
            const amount = amounttoreduce;
            amounttoreduce = 0;
            return {
              ...item,
              cisamount: item.cisamount - amount,
              cisdueamount: item.cisamount - amount
            };
          } else return { ...item };
        });
        cisamount = sumBy(newValues, 'cisamount');
      }

      form.setFieldsValue({
        [fieldName]: newValues,
        cisamount
      });
    },
    [form]
  );
  const [state] = useState<any>({
    payerList: []
  });

  // eslint-disable-next-line no-unused-vars
  const onChangePayer = (e: any) => {
    const selectedid = Number(e);
    const list = state.payerList;
    const res = list.filter((rowItem: any) => rowItem.tpid === selectedid)[0];
    return res;
  };

  const insertpayer = (obj: any, index: any) => {
    const values = form.getFieldValue(
      fieldName
    ) as ClientInvoiceScheduleResponse[];
    const newValues = values.map((item, i) => {
      if (i !== index) {
        return item;
      }

      let newDate = moment().add(
        obj?.tppaydelayin
          ?.replace('M', '')
          .substr(1, obj?.tppaydelayin?.length),
        'days'
      );
      if (obj?.tppaydelayin?.indexOf('M') > -1) {
        newDate = moment(newDate).endOf('month');
      }

      const tpbkid = obj?.tpbklist.find(
        (bk: any) =>
          bk.bkstatus === 'VALID' && ['INOUT', 'OUT'].includes(bk.bktype)
      )?.tpbkid;
      const updatedItem = {
        ...item,
        tpbkid: tpbkid,
        cispaymentdelaycode: obj?.tppaydelayin ? obj?.tppaydelayin : '',
        cispaymentmode: obj?.tppaymodein ? obj?.tppaymodein : '',
        tpbeneficiarybankaccount: tpbkid,
        tpidpayer: obj?.tpid ? obj?.tpid : undefined,
        tppayerbankaccount: tpbkid,
        cisduedate: newDate,
        tpbklist: obj?.tpbklist
      };

      return {
        ...updatedItem
      };
    });
    form.setFieldsValue({
      [fieldName]: newValues
    });
  };

  const columns = useMemo<DataGridColDef<ClientInvoiceScheduleResponse>[]>(
    () => [
      {
        comcode: 'CILinesSchedule.cistype',
        key: 'cistype',
        width: 150,
        fixed: 'left',
        render: (_, record, index) => (
          <Form.Item
            name={getName('cistype')(record)}
            rules={[
              {
                required:
                  record.cispaymentmode === 'TRANSFER' ||
                  record.cispaymentmode === 'DDEBIT'
              }
            ]}
          >
            <ParListSelect parcode="CISTYPE" disabled={ciid !== undefined} />
          </Form.Item>
        )
      },
      {
        comcode: 'CILinesSchedule.tpidpayer',
        key: 'tpidpayer',
        width: 270,
        render: (_, record, index) => (
          <Form.Item
            name={getName('tpidpayer')(record)}
            rules={[
              {
                required:
                  record.cispaymentmode === 'TRANSFER' ||
                  record.cispaymentmode === 'DDEBIT'
              }
            ]}
            key={index}
          >
            <ThirdPartyPayerList
              selectedV={form.getFieldValue(getName('tpidpayer')(record))}
              disabled={ciid !== undefined}
              key={index}
              onSelected={(e) => insertpayer(e, index)}
              tpid={form.getFieldValue(['thirdparty', 'tpid'])}
            />
          </Form.Item>
        )
      },
      {
        comcode: 'CILinesSchedule.cisamount',
        dataIndex: 'cisamount',
        width: 130,
        render: (_value, _record, index) => (
          <Form.Item
            name={getName('cisamount')(_record)}
            rules={[
              {
                required:
                  _record.cispaymentmode === 'TRANSFER' ||
                  _record.cispaymentmode === 'DDEBIT'
              }
            ]}
          >
            <InputCurrency
              currcode={form.getFieldValue('currcode')}
              onChange={(value) => recalculate(index, { cisamount: value })}
              disabled={ciid !== undefined}
            />
          </Form.Item>
        )
      },
      {
        comcode: 'CILinesSchedule.cispaymentdelaycode',
        key: 'cispaymentdelaycode',
        width: 250,
        render: (_, record, index) => (
          <Form.Item
            name={getName('cispaymentdelaycode')(record)}
            rules={[
              {
                required:
                  record.cispaymentmode === 'TRANSFER' ||
                  record.cispaymentmode === 'DDEBIT'
              }
            ]}
          >
            <ParListSelect
              parcode="PAYDELAYIN"
              onChange={(value) => {
                let newDate = moment().add(
                  value.replace('M', '').substr(1, value.length),
                  'days'
                );
                if (value.indexOf('M') > -1) {
                  newDate = moment(newDate).endOf('month');
                }
                recalculate(index, {
                  cisduedate: newDate
                });
              }}
              disabled={ciid !== undefined}
            />
          </Form.Item>
        )
      },
      {
        comcode: 'CILinesSchedule.cisduedate',
        key: 'cisduedate',
        width: 130,
        render: (_, record, index) => (
          <Form.Item
            name={getName('cisduedate')(record)}
            rules={[
              {
                required:
                  record.cispaymentmode === 'TRANSFER' ||
                  record.cispaymentmode === 'DDEBIT'
              }
            ]}
          >
            {ciid ? (
              dateToString(record.cisduedate)
            ) : (
              <DatePicker value={moment(record.cisduedate)} />
            )}
          </Form.Item>
        )
      },
      {
        comcode: 'CILinesSchedule.cispaymentmode',
        key: 'cispaymentmode',
        width: 150,
        render: (_, record, index) => (
          <Form.Item
            name={getName('cispaymentmode')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect parcode="PAYMODEIN" disabled={ciid !== undefined} />
          </Form.Item>
        )
      },
      {
        comcode: 'CILinesSchedule.tppayerbankaccount',
        key: 'tppayerbankaccount',
        width: 210,
        render: (_, record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name={getName('tppayerbankaccount')(record)}
                rules={[
                  {
                    required:
                      record.cispaymentmode === 'TRANSFER' ||
                      record.cispaymentmode === 'DDEBIT'
                  }
                ]}
              >
                <CIPayerSelect
                  tpid={String(record.tpidpayer)}
                  selectedPayer={getFieldValue(getName('tpidpayer')(record))}
                  disabled={ciid !== undefined}
                />
              </Form.Item>
            )}
          </Form.Item>
        )
      },
      {
        comcode: 'mgbankaccount',
        key: 'mgbankaccount',
        width: 150,
        render: (value, record, index) => (
          <Form.Item shouldUpdate={shouldUpdateCompare('currcode')} noStyle>
            {({ getFieldValue }) => (
              <Form.Item
                name={getName('mgbankaccount')(record)}
                rules={[
                  {
                    required:
                      record.cispaymentmode === 'TRANSFER' ||
                      record.cispaymentmode === 'DDEBIT'
                  }
                ]}
                initialValue={value}
              >
                <ParBankAccountSelect
                  mgid={Number(localStorage.getItem('mgid'))}
                  direction="IN"
                  currcode={getFieldValue('currcode')}
                  defaultSelect
                  disabled={ciid !== undefined}
                />
              </Form.Item>
            )}
          </Form.Item>
        )
      },
      {
        comcode: 'CILinesSchedule.cisstatus',
        key: 'cisstatuslabel',
        width: 150,
        render: (_value, _record, index) => (
          <Form.Item name={getName('cisstatuslabel')(_record)}>
            <Value />
          </Form.Item>
        )
      },
      {
        comcode: 'CILinesSchedule.cisdueamount',
        key: 'cisdueamount',
        render: (_value, _record, index) => (
          <Form.Item name={getName('cisdueamount')(_record)}>
            <Value
              formatter={(value) =>
                toCurrency(value, form.getFieldValue('currcode'))
              }
            />
          </Form.Item>
        ),
        width: 100
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_value, record, index) => (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => {
              form.setFieldsValue({
                cisamount: form.getFieldValue('cisamount') - record.cisamount
              });
              remove(record.cisid);
            }}
          >
            <DeleteButton onlyIcon />
          </Popconfirm>
        ),
        fixed: 'right',
        width: 70
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recalculate, ciid]
  );

  const title = (
    <>
      <Col span={16} />
      <Form.Item
        noStyle
        shouldUpdate={shouldUpdateCompare(['cisamount', 'cigrosstotal'])}
      >
        {({ getFieldValue }) => {
          return (
            <Space size={16}>
              <span style={{ color: '#668cff' }}>
                <GetLabel label="TotalInvoiceAmount" /> :
                {toCurrency(
                  getFieldValue('cigrosstotal'),
                  getFieldValue('currcode')
                )}
              </span>
              <strong
                style={{
                  color:
                    getFieldValue('cisamount') === getFieldValue('cigrosstotal')
                      ? 'green'
                      : getFieldValue('cisamount') >
                        getFieldValue('cigrosstotal')
                      ? 'red'
                      : 'orange'
                }}
              >
                <GetLabel label="TotalPaymentsScheduled" /> :
                {toCurrency(
                  getFieldValue('cisamount'),
                  getFieldValue('currcode')
                )}
              </strong>
            </Space>
          );
        }}
      </Form.Item>
    </>
  );

  return (
    <div style={{ backgroundColor: '#f7f7f7', padding: 5 }}>
      <Title comcode="PaymentSchedule" level={5}></Title>
      <div style={{ padding: 10, background: 'white' }}>
        <div></div>
        <Form.Item
          noStyle
          shouldUpdate={shouldUpdateCompare('clientInvoiceSchedules')}
        >
          {({ getFieldValue }) =>
            ciid === undefined ? (
              <DataGrid
                title={title}
                columns={columns}
                dataSource={getFieldValue(fieldName)}
                rowKey="cisid"
                scroll={{ x: 1200, y: 360 }}
                hideFilters
                extraActions={
                  <ButtonOperation
                    icon={<PlusOutlined />}
                    type="primary"
                    onClick={addRow}
                    comcode="add"
                    disabled={
                      getFieldValue(['thirdparty', 'tpid']) === undefined
                    }
                  />
                }
              />
            ) : (
              <DataGrid
                title={title}
                hideFilters
                columns={columns.filter((colum) => colum.key !== 'actions')}
                dataSource={getFieldValue(fieldName)}
                scroll={{ x: 1200, y: 360 }}
                rowKey="cisid"
              />
            )
          }
        </Form.Item>
      </div>
    </div>
  );
};

export default TabClientInvoiceSchedule;
